import { DAY_UNIT } from '../constants';

const MINUTES_IN_DAY = 1440;

class LeaveService {
  constructor(
    EventCollection,
    Authentication,
    Utils,
    PersonaCollection,
    sdk,
    toDateLongFilter,
    toTimeFilter,
    $q,
    $rootScope
  ) {
    'ngInject';

    Object.assign(this, {
      EventCollection,
      Authentication,
      Utils,
      PersonaCollection,
      sdk,
      toDateLongFilter,
      toTimeFilter,
      $q,
      $rootScope,
    });

    const { getLeaveTypeUsed, getTimeOffYear, computeRemainingDays } = this.sdk('timeOffsUtils');
    this.getTimeOffYear = getTimeOffYear;
    this.getLeaveTypeUsed = getLeaveTypeUsed;
    this.computeRemainingDays = computeRemainingDays;

    this.init();
  }

  init() {
    this.timeOffTypes = [];
    this.availableTimeOffTypes = [];

    this.$rootScope.$on('socket:user:caps:sync', (e, data) => {
      this.EventCollection.syncUserLeaveCaps(data.userId);
    });

    this.$rootScope.$on('sdk:timeOffs:save', (e, data) => {
      this.timeOffTypes = data;
      this.setAvailableTimeOffTypes();
    });
  }

  get units() {
    return this.Authentication.user?.orgSettings.leaveCapUnit;
  }

  get isGlobal() {
    return this.Authentication.user?.orgSettings.useGlobalLeaveCap;
  }

  get globalCap() {
    return this.Authentication.user?.orgSettings.leaveCap;
  }

  get workDayMinutes() {
    return this.Authentication.user.orgSettings.workDayMinutes;
  }

  getUserLeavesStatus(userId, date) {
    const user = this.PersonaCollection.personasDict[userId];
    const { leaveResetType, leaveResetDate } = this.Authentication.user.orgSettings;
    const year = this.getTimeOffYear({
      date,
      leaveResetType,
      leaveResetDate,
      hireDate: user.hireDate,
    });

    if (
      this.Authentication.isOrgFeatureEnabled('leaveTypes') &&
      this.Authentication.user.org.isPremium
    ) {
      return this.EventCollection.getUserLeavesStatus(userId, { year });
    }

    return {};
  }

  async loadTimeOffTypes() {
    this.timeOffTypes = await this.EventCollection.getTimeOffTypes();
    this.setAvailableTimeOffTypes();
  }

  async saveTimeOffTypes(typesToSave) {
    this.timeOffTypes = await this.EventCollection.saveTimeOffTypes(typesToSave);
    this.setAvailableTimeOffTypes();
  }

  getNoShowSummary(userId) {
    if (this.Authentication.isPrefab && this.Authentication.isOrgFeatureEnabled('noShow')) {
      return this.EventCollection.getNoShowSummary(userId);
    }
    return {};
  }

  getSickCalloutSummary(userId) {
    if (this.Authentication.isPrefab && this.Authentication.isOrgFeatureEnabled('sickCallout')) {
      return this.EventCollection.getSickCalloutSummary(userId);
    }
    return {};
  }

  setAvailableTimeOffTypes() {
    this.availableTimeOffTypes = this.timeOffTypes.filter((t) => t.enabled && t.available);
  }

  getGlobalCap(userCaps) {
    return userCaps?.global?.cap || this.globalCap;
  }

  getCapForType(type, userCaps) {
    const _getCapForType = this.sdk('timeOffsUtils').getCapForType;
    return _getCapForType(type, { userCaps, useGlobal: this.isGlobal, globalCap: this.globalCap });
  }

  isCapSet(type, userCaps) {
    const cap = this.getCapForType(type, userCaps);
    return !!cap || cap === 0;
  }

  getCapForTypeInMinutes(type, userCaps) {
    const cap = this.getCapForType(type, userCaps) || 0;
    return this.units === DAY_UNIT ? cap * this.workDayMinutes : cap * 60;
  }

  getUsedLeaveDisplayDuration(type, leaveStatus = {}) {
    const timeUsed = this.getLeaveTypeUsed(type, leaveStatus);
    return this.getDecimalLabel(timeUsed);
  }

  getDecimalLabel(usedMinutes) {
    const _getDecimalLabel = this.sdk('timeOffsUtils').getDecimalLabel;
    return _getDecimalLabel(usedMinutes, {
      leaveCapUnit: this.units,
      workDayMinutes: this.workDayMinutes,
      daysUnit: DAY_UNIT,
    });
  }

  getDescriptiveLabel(usedMinutes) {
    if (!usedMinutes) {
      return '0';
    }

    let label = '';
    const days = Math.floor(usedMinutes / this.workDayMinutes);
    const hours = Math.floor((usedMinutes % this.workDayMinutes) / 60);
    const minutes = (usedMinutes % this.workDayMinutes) % 60;

    if (this.units === DAY_UNIT && !hours && !minutes) {
      return days;
    }

    label += days ? `${days}d ` : '';
    label += hours ? `${hours}h ` : '';
    label += minutes ? `${minutes}m` : '';

    return label;
  }

  getDurationTime(usedMinutes, useSolarDays = true) {
    const minutesInDay = useSolarDays ? 24 * 60 : this.workDayMinutes;
    const days = this.computeRemainingDays(usedMinutes, minutesInDay);
    const hours = Math.floor((usedMinutes % minutesInDay) / 60);
    const minutes = (usedMinutes % minutesInDay) % 60;

    return this.getDurationTimeLabel(days, hours, minutes);
  }

  getDurationTimeLabel(days, hours, minutes) { // eslint-disable-line
    if (days && hours && minutes) {
      const roundedHours = Math.abs(hours);
      const roundedMinutes = Math.abs(minutes);
      return `${days} d ${roundedHours} h ${roundedMinutes} m`;
    }

    if (days && hours) {
      const roundedHours = Math.abs(hours);
      return `${days} ${Math.abs(days) === 1 ? 'day' : 'days'}, ${roundedHours} ${
        roundedHours === 1 ? 'hour' : 'hours'
      }`;
    }

    if (hours && minutes) {
      return `${hours} ${Math.abs(hours) === 1 ? 'hour' : 'hours'}, ${Math.abs(minutes)} min`;
    }

    if (days && minutes) {
      return `${days} ${Math.abs(days) === 1 ? 'day' : 'days'}, ${Math.abs(minutes)} min`;
    }

    if (hours) {
      return Math.abs(hours) === 1 ? `${hours} hour` : `${hours} hours`;
    }

    return Math.abs(days) === 1 ? `${days} day` : `${days} days`;
  }

  isLimitReached(type, leavesStatus = {}, userCaps) {
    const capsForType = this.getCapForType(type, userCaps);
    if (!capsForType || !type.paid) {
      return false;
    }

    const usedLeave = this.getLeaveUsed(type, leavesStatus);
    const capsInMinutes =
      this.units === DAY_UNIT
        ? capsForType * (this.Authentication.user.orgSettings.workDayMinutes || MINUTES_IN_DAY)
        : capsForType * 60;
    return Number(usedLeave) >= Number(capsInMinutes);
  }

  getPaidLeavesDuration(leavesStatus) {
    return Object.values(leavesStatus).reduce((acc, item) => acc + (item.paid || 0), 0);
  }

  getUnpaidLeavesDuration(leavesStatus) {
    return Object.values(leavesStatus).reduce((acc, item) => acc + (item.unpaid || 0), 0);
  }

  getLeaveUsed(type, leavesStatus) {
    return this.isGlobal
      ? this.getPaidLeavesDuration(leavesStatus)
      : this.getLeaveTypeUsed(type, leavesStatus);
  }

  _stripHours(date) {
    return this.Utils.getRealDate(this.Utils.formatDate(date));
  }

  async getRequestedAndRemainingLeaveTimes(timeOff, timeOffType) {
    const { leaveTypesEnabled } = this.Authentication.user.orgSettings;

    const dtstart = timeOff.fullDay ? this._stripHours(timeOff.fullDayDtstart) : timeOff.dtstart;
    const dtend = timeOff.fullDay
      ? this._stripHours(this.Utils.addDays(timeOff.fullDayDtend, 1))
      : timeOff.dtend;

    const durationInMinutes = (dtend - dtstart) / 1000 / 60;
    const requested = this.getDurationTime(durationInMinutes);
    let remaining;

    if (leaveTypesEnabled && timeOffType && timeOff.user) {
      const [leaveStatus, userLeaveCaps] = await this.getUserLeaveData(timeOff);
      const usedMinutes = this.getLeaveUsed(timeOffType, leaveStatus);
      const cap = this.getCapForTypeInMinutes(timeOffType, userLeaveCaps);

      if (cap) {
        remaining = this.getDurationTime(Math.max(0, cap - usedMinutes), false);
      }
    }

    return { requested, remaining };
  }

  async getUserLeaveData(timeOff) {
    const asyncCalls = [this.getUserLeavesStatus(timeOff.user.id, timeOff.dtstart)];
    asyncCalls.push(this.getUserCaps(timeOff.user.id));

    const [leaveStatus, leaveCapsList = []] = await this.$q.all(asyncCalls);
    const userLeaveCaps = leaveCapsList.reduce(
      (agg, cap) => ((agg[cap.leaveTypeId || 'global'] = cap), agg),
      {}
    );

    return [leaveStatus, userLeaveCaps];
  }

  isTypeEnabled(typeId) {
    return (
      this.Authentication.user.orgSettings.leaveTypesEnabled &&
      this.availableTimeOffTypes.findIndex((t) => t.type === typeId) > -1
    );
  }

  isLeaveSickEnabled() {
    const { LEAVE_SICK_TYPE } = this.sdk('constants');
    return this.isTypeEnabled(LEAVE_SICK_TYPE);
  }

  async getUserCaps(userId, { asDict = false } = {}) {
    const capsList = await this.EventCollection.getUserCaps(userId);

    if (asDict) {
      return (capsList || []).reduce(
        (agg, cap) => ((agg[cap.leaveTypeId || 'global'] = cap), agg),
        {}
      );
    }

    return capsList;
  }

  updateUserCaps(user, payload) {
    return this.EventCollection.updateUserCaps(user.id, payload);
  }

  shouldShowRemainingField(timeOffType, remaining) {
    return timeOffType?.paid && remaining !== undefined;
  }

  _toRequestDurationText(leave) {
    let minutes;
    let leaveDays;
    if (leave.fullDay) {
      leaveDays =
        Math.floor(this.Utils.subtractDates(leave.dtendDate, leave.dtstartDate, 'minutes') + 1) /
        60 /
        24; // full day leaves end at 23:59
      minutes = leaveDays * this.Authentication.user.orgSettings.workDayMinutes;
    } else {
      minutes = this.Utils.subtractDates(leave.dtendDate, leave.dtstartDate, 'minutes');

      if (
        this.Authentication.user.orgSettings.shiftBreakType ===
          this.sdk('constants').UNPAID_BREAKS &&
        leave.breakDuration
      ) {
        minutes = Math.max(0, minutes - leave.breakDuration);
      }
    }

    if (this.Authentication.user.orgSettings.leaveTypesEnabled) {
      if (this.units === 'DAYS') {
        leaveDays = this.Utils.roundNumber(minutes / this.workDayMinutes);

        if (!leave.fullDay) {
          leaveDays = Math.min(leaveDays, 1);
        }

        return `${leaveDays} ${leaveDays === 1 ? 'day' : 'days'}`;
      }

      return this.Utils.getDisplayDuration(minutes);
    }

    if (leave.fullDay) {
      return `${leaveDays} ${leaveDays === 1 ? 'day' : 'days'}`;
    }

    return this.Utils.getDisplayDuration(minutes);
  }

  _toRequestFromToText(leave) {
    const toDate = (date) => this.toDateLongFilter(date, 'EEE');
    if (leave.fullDay) {
      return `${toDate(leave.dtstartDate)} - ${toDate(leave.dtendDate)}`;
    }

    return `${toDate(leave.dtstartDate)} ${this.toTimeFilter(
      leave.dtstartDate
    )} - ${this.toTimeFilter(leave.dtendDate)}`;
  }

  async getUserDetailedLeaveReport(userId, year) {
    const result = await this.$q.all([
      this.Authentication.sendRequest({
        path: `reports/leave/users/${userId}/detailed?year=${year}`,
      }),
      this.loadTimeOffTypes(),
    ]);

    const reportData = result[0].data;

    const leaveTypeDict = this.timeOffTypes.reduce(
      (prev, leaveType) => ((prev[leaveType.id] = leaveType), prev),
      {}
    );

    const statusTextMap = {
      cancelled: 'Denied',
      published: 'Approved',
      pending_admin: 'Pending',
    };

    reportData.leaves.forEach((leave) => {
      leave.typeName = leaveTypeDict[leave.leaveTypeId]?.name;
      leave.dtstartDate = this.Utils.getRealDate(leave.dtstart);
      leave.dtendDate = this.Utils.getRealDate(leave.dtend);
      leave.requestFromToText = this._toRequestFromToText(leave);
      leave.requestDurationText = this._toRequestDurationText(leave);
      leave.sortBy = -leave.dtstartDate.getTime();
      leave.statusText = statusTextMap[leave.status];
    });

    reportData.leaves = this.Utils.sortBy(reportData.leaves, ['sortBy']);

    return reportData;
  }
}

export default LeaveService;
